import React, {useMemo, useRef, useState} from 'react';
import {Col, FormGroup, Input, Label, Row} from 'reactstrap';
import {Form} from '@unform/web';
import I18nMessage from '../../components/I18nMessage';
import ReactInputCpfCnpj from '../../components/react-mask-cpf-cnpj';
import ReactMaskPhone from '../../components/react-mask-phone';
import ReactMaskZipcode from '../../components/react-mask-zipcode';
import ReactSelect from '../../components/react-select';
import {TextArea} from '../../components/textarea';
import {FieldSet} from '../../components/fieldset';
import {Input as UInput} from '../../components/input';
import ToggleButton from '../../components/toggle-button';
import {GroupButtonForm} from '../../components/group-button-form';
import DatePicker from '../../components/react-datepicker';
import * as moment from 'moment';
import {PlatformIntl} from '../../infra/PlatformIntl';
import Yup from '../../infra/Yup';
import ViaCepService from '../../services/viacep-service';

const platformIntl = PlatformIntl.getInstance();

const schema = Yup.object().shape({
    id: Yup.number(),
    cpfCnpj: Yup.string()
        .label(platformIntl.formatMessage('customerCpfCnpj'))
        .required(),
    type: Yup.string()
        .label(platformIntl.formatMessage('customerType'))
        .required(),
    name: Yup.string()
        .label(platformIntl.formatMessage('customerName'))
        .required(),
    street: Yup.string()
        .label(platformIntl.formatMessage('customerStreet'))
        .required(),
    number: Yup.string()
        .label(platformIntl.formatMessage('customerNumber'))
        .required(),
    complement: Yup.string().label(platformIntl.formatMessage('customerComplement')),
    neighborhood: Yup.string()
        .label(platformIntl.formatMessage('customerNeighborhood'))
        .required(),
    city: Yup.string()
        .label(platformIntl.formatMessage('customerCity'))
        .required(),
    state: Yup.string()
        .label(platformIntl.formatMessage('customerState'))
        .required(),
    postalCode: Yup.string()
        .label(platformIntl.formatMessage('customerPostalCode'))
        .required(),
    phone: Yup.string()
        .label(platformIntl.formatMessage('customerPhone'))
        .required(),
    cellphone: Yup.string().label(platformIntl.formatMessage('customerCellphone')),
    notificationEmail: Yup.string()
        .label(platformIntl.formatMessage('customerNotificationEmail'))
        .required(),
    secondaryEmail: Yup.string().label(platformIntl.formatMessage('customerSecondaryEmail')),
    category: Yup.string()
        .label(platformIntl.formatMessage('customerCategory'))
        .required(),
    status: Yup.string()
        .label(platformIntl.formatMessage('customerStatus'))
        .required(),
    notes: Yup.string().label(platformIntl.formatMessage('customerNotes')),
    disabled: Yup.boolean().label(platformIntl.formatMessage('customerDisabled')),
    registrationDate: Yup.date()
        .label(platformIntl.formatMessage('customerRegistrationDate'))
        .required(),
    expirationDate: Yup.date()
        .label(platformIntl.formatMessage('customerExpirationDate'))
        .nullable(true),
    invoiceDay: Yup.number().label(platformIntl.formatMessage('customerInvoiceDay')),
});

const initialize = (src) => {
    const data = Object.assign({}, src);
    if (!data.hasOwnProperty('type')) {
        data.type = 'PF';
    }
    if (!data.hasOwnProperty('cpfCnpj')) {
        data.cpfCnpj = 'cpf';
    }
    return data;
};

const CustomerForm = (props) => {
    const {
        cancel,
        data,
        edit,
        newRegister,
        updateRegister,
        loading,
    } = props;

    const formRef = useRef(null);
    const [type, setType] = useState(data.type === 'PJ' ? 'cnpj' : 'cpf');
    const states = useMemo(() => {
        const _states = ['AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'];

        const stateOptions = _states.map((_state) => ({
            id: _state,
            title: _state,
        }));

        return stateOptions;
    }, []);

    const handleSubmit = async (customerData) => {
        let customer = Object.assign(data, customerData);
        try {
            await schema.validate(customer, {
                abortEarly: false,
            });
            customer = schema.cast(customerData);
            if (customer.expirationDate) {
                customer.expirationDate = moment(customer.expirationDate).toISOString();
            }
            if (customer.registrationDate) {
                customer.registrationDate = moment(customer.registrationDate).toISOString();
            }
            edit ? updateRegister(customer) : newRegister(customer);
        } catch (err) {
            const validationErrors = {};
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach((error) => {
                    validationErrors[error.path] = error.message;
                });
                formRef.current.setErrors(validationErrors);
            }
        }
    };

    const customerTypeOnChange = (_data) => {
        if (_data) {
            setType(_data.id === 'PF' ? 'cpf' : 'cnpj');
        }
    };

    const setOrClearField = (field, value) => {
        if (value) {
            formRef.current.setFieldValue(field, value);
        } else {
            formRef.current.clearField(field);
        }
    };

    const postalCodeOnChange = async (event) => {
        const postalCode = event.target.value.replace(/\D/g, '');
        if (postalCode.length !== 8) {
            return;
        }

        const address = await ViaCepService.getAddressByPostalCode(postalCode);

        setOrClearField('street', address?.logradouro);
        setOrClearField('complement', address?.complemento);
        setOrClearField('neighborhood', address?.bairro);
        setOrClearField('city', address?.localidade);
        setOrClearField('state', address?.uf);
    };

    return (
        <Form ref={formRef} onSubmit={handleSubmit} initialData={initialize(data)}>
            {!!edit && <UInput hidden type="number" name="id"/>}
            <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedIdentification"/>}>
                <>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="cpfCnpj"><I18nMessage id="customerCpfCnpj"/>*</Label>
                                <ReactInputCpfCnpj
                                    name="cpfCnpj"
                                    id="cpfCnpj"
                                    type={type}
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="name"><I18nMessage id="sharedName"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="name"
                                    id="name"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="type"><I18nMessage id="customerType"/>*</Label>
                                <ReactSelect id={'type'}
                                             name="type"
                                             onChange={customerTypeOnChange}
                                             options={[
                                                 {id: 'PF', title: platformIntl.formatMessage('customerTypePF')},
                                                 {id: 'PJ', title: platformIntl.formatMessage('customerTypePJ')},
                                             ]}/>
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="category"><I18nMessage id="customerCategory"/>*</Label>
                                <ReactSelect id={'category'}
                                             name="category"
                                             options={[
                                                 {id: 'client', title: platformIntl.formatMessage('customerCategoryClient')},
                                                 {id: 'partner', title: platformIntl.formatMessage('customerCategoryPartner')},
                                                 {id: 'monitoring', title: platformIntl.formatMessage('customerCategoryMonitoring')},
                                             ]}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            </FieldSet>
            <br/>
            <FieldSet className="text-left border p-4" title={platformIntl.formatMessage('customerContact')}>
                <>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="phone"><I18nMessage id="customerPhone"/>*</Label>
                                <ReactMaskPhone
                                    tag={UInput}
                                    type="number"
                                    name="phone"
                                    id="phone"
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="cellphone"><I18nMessage id="customerCellphone"/></Label>
                                <ReactMaskPhone
                                    tag={UInput}
                                    type="number"
                                    name="cellphone"
                                    id="cellphone"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="notificationEmail"><I18nMessage id="customerNotificationEmail"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="notificationEmail"
                                    id="notificationEmail"
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="secondaryEmail"><I18nMessage id="customerSecondaryEmail"/></Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="secondaryEmail"
                                    id="secondaryEmail"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            </FieldSet>
            <br/>
            <FieldSet className="text-left border p-4" title={<I18nMessage id="sharedAddress"/>}>
                <>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="postalCode"><I18nMessage id="customerPostalCode"/>*</Label>
                                <ReactMaskZipcode
                                    name="postalCode"
                                    id="postalCode"
                                    onChange={postalCodeOnChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="street"><I18nMessage id="customerStreet"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="street"
                                    id="street"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="number"><I18nMessage id="customerNumber"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="number"
                                    id="number"
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="complement"><I18nMessage id="customerComplement"/></Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="complement"
                                    id="complement"
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="neighborhood"><I18nMessage id="customerNeighborhood"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="neighborhood"
                                    id="neighborhood"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="city"><I18nMessage id="customerCity"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="city"
                                    id="city"
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="state"><I18nMessage id="customerState"/>*</Label>
                                <ReactSelect id={'state'}
                                             name="state"
                                             options={states}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            </FieldSet>
            <br/>
            <FieldSet className="text-left border p-4" title={<I18nMessage id="customerRegistrationSection"/>}>
                <>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="status"><I18nMessage id="customerStatus"/>*</Label>
                                <ReactSelect id={'status'}
                                             name="status"
                                             options={[
                                                 {id: 'active', title: platformIntl.formatMessage('customerStatusActive')},
                                                 {id: 'frozen', title: platformIntl.formatMessage('customerStatusFrozen')},
                                                 {id: 'inactive', title: platformIntl.formatMessage('customerStatusInactive')},
                                                 {id: 'trial', title: platformIntl.formatMessage('customerStatusTrial')},
                                             ]}
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="disabled"><I18nMessage id="customerDisabled"/></Label>
                                <ToggleButton
                                    name={'disabled'}
                                    id={'disabled'}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="invoiceDay"><I18nMessage id="customerInvoiceDay"/>*</Label>
                                <Input
                                    tag={UInput}
                                    type="text"
                                    name="invoiceDay"
                                    id="invoiceDay"
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="registrationDate"><I18nMessage id="customerRegistrationDate"/>*</Label>
                                <DatePicker
                                    name={'registrationDate'}
                                    displayFormat={'dd/MM/yyyy'}
                                />
                            </FormGroup>
                        </Col>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="expirationDate"><I18nMessage id="customerExpirationDate"/></Label>
                                <DatePicker
                                    name={'expirationDate'}
                                    displayFormat={'dd/MM/yyyy'}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <FormGroup className="custom-formGroup">
                                <Label for="notes"><I18nMessage id="customerNotes"/></Label>
                                <TextArea
                                    type="text"
                                    name="notes"
                                    id="notes"
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                </>
            </FieldSet>
            <GroupButtonForm
                loading={loading}
                cancel={cancel}
                submit={() => formRef.current.submitForm()}
            />
        </Form>
    );
};

export default CustomerForm;
